import * as React from "react";
import debounce from "lodash.debounce";

// // // //

/**
 * OnChangeCallback
 * Represents a callback function that is triggered when the value is changed.
 @typeparam T - The type of the updated value.
 @param updatedValue - The updated value.
 */
type OnChangeCallback<T> = (updatedValue: T) => void;

/**
 * DebouncedInputProps
 * Represents the properties required for the useDebouncedState props
 * @typeparam T - The type of the input value.
 * @property value - The current value of the input.
 * @property debounceTimeout - The timeout value for debouncing the input change.
 * @property onChange - The callback function to be invoked when the value changes.
 */
interface Props<T> {
    value: T;
    debounceTimeout: number;
    onChange: OnChangeCallback<T>;
}

const valuesEqual = (left: unknown, right: unknown): boolean => {
    if (Array.isArray(left) && Array.isArray(right)) {
        return (
            left.length === right.length &&
            left.every((value, index) => value === right[index])
        );
    }

    return Object.is(left, right);
};

/**
 * useDebouncedState
 * React.useState hook with built-in debounce behavior
 * @typeparam T - The type of the state value.
 * @param props - The properties required for the debounced state.
 * @returns An array containing the internal value and the onChange callback.
 */
export function useDebouncedState<T>(
    props: Props<T>,
): [T, OnChangeCallback<T>] {
    const { value, debounceTimeout = 0 } = props;

    // Manage the internal value state
    const [internalValue, setInternalValue] = React.useState(value);

    const onChangeRef = React.useRef(props.onChange);
    onChangeRef.current = props.onChange;

    // Debounce the setInputValue function
    const setInputValue = React.useCallback(
        debounce((updatedValue: T) => {
            onChangeRef.current(updatedValue);
        }, debounceTimeout),
        [debounceTimeout],
    );

    // Cancel pending debounce on unmount or when deps change
    React.useEffect(() => {
        return () => {
            setInputValue.cancel();
        };
    }, [setInputValue]);

    // Update internalValue when props.value has changed
    React.useEffect(() => {
        setInternalValue((current) => {
            if (valuesEqual(props.value, current)) {
                return current;
            }

            return props.value;
        });
    }, [props.value]);

    // Update the internal value state and invokes the debounced setInputValue function
    function onChange(updatedValue: T) {
        setInternalValue(updatedValue);
        setInputValue(updatedValue);
    }

    // Return the internal value and onChange callback (like standard "useState" hook)
    return [internalValue, onChange];
}
